// ─────────────────────────────────────────────────────────────────────────
// Cartolas bancarias (26-ago-2026, Fase E área contable).
//
// Se sube la cartola descargada del portal del banco (Santander o Banco de
// Chile, .csv o .xlsx) → el backend parsea, deduplica y concilia solo los
// montos EXACTOS contra facturas del SII. Lo que queda pendiente se concilia
// a mano acá mismo: cada movimiento pendiente despliega candidatos (facturas
// con monto cercano ±1%). Verde = abono (nos depositaron), rojo = cargo.
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from "react";
import backend from "../../backend";
import useIsMobile from "../../hooks/useIsMobile";
import { formatPrice } from "../../utils";

const fmt = n => `${n < 0 ? "−" : ""}$${formatPrice(Math.round(Math.abs(n || 0)))}`;
const fmtD = d => d ? new Date(d).toLocaleDateString("es-CL", { day: "2-digit", month: "short" }) : "—";
const num = { fontVariantNumeric: "tabular-nums" };

const BANCO_LABEL = { santander: "Santander", chile: "Banco de Chile" };

const S = {
  card:  { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: 14 },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" },
  input: {
    background: "#161616", border: "1px solid #333", borderRadius: 6,
    padding: "8px 10px", fontSize: 12, color: "#ddd", boxSizing: "border-box",
    fontFamily: "Arial,sans-serif",
  },
  btnRojo: (disabled) => ({
    background: disabled ? "#333" : "#e30613", color: "#fff", border: "none",
    borderRadius: 8, padding: "8px 16px", fontSize: 12, fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.7 : 1, minHeight: 38,
  }),
  // Tabla densa desktop (28-ago, patrón Pagos.js)
  th: {
    background: "#1f1f1f", padding: "8px 12px", textAlign: "left", fontSize: 10,
    fontWeight: 700, color: "#999", borderBottom: "1px solid #2e2e2e",
    textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap",
  },
  td: {
    padding: "8px 12px", color: "#bbb", borderBottom: "1px solid #252525",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  chip: (on) => ({
    background: on ? "#3a1114" : "#1e1e1e", color: on ? "#ff8a8f" : "#999",
    border: `1px solid ${on ? "#e30613" : "#333"}`,
    borderRadius: 6, padding: "5px 12px", fontSize: 11, cursor: "pointer", whiteSpace: "nowrap",
  }),
};

const mesActual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const Cartolas = () => {
  // Pulido desktop 27-ago: hover sutil en los movimientos (solo desktop);
  // el layout ya usa el ancho y los montos van a la derecha con tabular-nums.
  const isMobile = useIsMobile();
  // subida
  const [bancoUp, setBancoUp] = useState("santander");
  const [archivo, setArchivo] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [resultado, setResultado] = useState(null); // { ok, msg }

  // listado
  const [mes, setMes] = useState(mesActual());
  const [filtroBanco, setFiltroBanco] = useState(""); // "" = todos
  const [filtroConc, setFiltroConc] = useState("todos"); // todos | pendientes | conciliados
  const [movs, setMovs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // conciliación manual
  const [abierto, setAbierto] = useState(null);      // _id del mov con candidatos desplegados
  const [candidatos, setCandidatos] = useState([]);
  const [cargandoCand, setCargandoCand] = useState(false);
  const [conciliando, setConciliando] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { mes };
      if (filtroBanco) params.banco = filtroBanco;
      const r = await backend.get("cartola/movimientos", { params });
      setMovs(r.data || []);
      setError(null);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    setLoading(false);
  }, [mes, filtroBanco]);

  useEffect(() => { cargar(); }, [cargar]);

  const subir = async () => {
    if (!archivo) return;
    setSubiendo(true);
    setResultado(null);
    try {
      const fd = new FormData();
      fd.append("archivo", archivo);
      fd.append("banco", bancoUp);
      const r = await backend.post("cartola/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data;
      setResultado({
        ok: true,
        msg: `✅ ${d.insertados} movimientos nuevos · ${d.duplicados} duplicados omitidos · ${d.conciliadosAuto} conciliados automáticamente${d.errores?.length ? ` · ${d.errores.length} filas con problemas` : ""}`,
      });
      setArchivo(null);
      await cargar();
    } catch (e) {
      setResultado({ ok: false, msg: `❌ ${e?.response?.data?.error || e.message}` });
    }
    setSubiendo(false);
  };

  const abrirCandidatos = async (mov) => {
    if (abierto === mov._id) { setAbierto(null); return; }
    setAbierto(mov._id);
    setCandidatos([]);
    setCargandoCand(true);
    try {
      const r = await backend.get(`cartola/movimientos/${mov._id}/candidatos`);
      setCandidatos(r.data || []);
    } catch { setCandidatos([]); }
    setCargandoCand(false);
  };

  const conciliar = async (mov, cand) => {
    setConciliando(mov._id);
    try {
      // cand = null → desconciliar
      await backend.patch(`cartola/movimientos/${mov._id}/conciliar`,
        cand ? { tipo: cand.tipo, refId: cand._id } : { tipo: null });
      setAbierto(null);
      await cargar();
    } catch (e) { alert(e?.response?.data?.error || e.message); }
    setConciliando(null);
  };

  // ── KPIs del mes (sobre lo filtrado) ──
  const abonos = movs.filter(m => m.monto > 0).reduce((s, m) => s + m.monto, 0);
  const cargos = movs.filter(m => m.monto < 0).reduce((s, m) => s + Math.abs(m.monto), 0);
  const conciliados = movs.filter(m => m.conciliadoCon?.tipo).length;
  const pct = movs.length ? Math.round((conciliados / movs.length) * 100) : 0;

  // Filtro por estado de conciliación (28-ago)
  const visibles = movs.filter(m => {
    const conc = !!m.conciliadoCon?.tipo;
    return filtroConc === "pendientes" ? !conc : filtroConc === "conciliados" ? conc : true;
  });

  // Candidatos de conciliación manual — mismo bloque en tarjeta (móvil) y
  // fila expandida de la tabla (desktop).
  const renderCandidatos = (m, esAbono) => (
    <div>
      <div style={{ ...S.label, marginBottom: 6 }}>
        Facturas de {esAbono ? "venta" : "compra"} con monto cercano (±1%)
      </div>
      {cargandoCand ? (
        <div style={{ fontSize: 11, color: "#8a8a8a" }}>Buscando candidatos…</div>
      ) : candidatos.length === 0 ? (
        <div style={{ fontSize: 11, color: "#8a8a8a" }}>
          Sin candidatos con monto cercano. Puede ser un pago agrupado de varias facturas,
          una remuneración u otro movimiento sin factura.
        </div>
      ) : (
        candidatos.map(c => (
          <div key={c._id} style={{
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
            padding: "7px 0", borderBottom: "1px solid #2a2a2a",
          }}>
            <div style={{ flex: 1, minWidth: 150 }}>
              <span style={{ fontFamily: "monospace", color: "#3498db", fontWeight: 700, fontSize: 12 }}>F°{c.folio}</span>
              <span style={{ fontSize: 11, color: "#ddd", marginLeft: 8 }}>{c.razon || "—"}</span>
              <span style={{ fontSize: 10, color: "#8a8a8a", marginLeft: 8 }}>{fmtD(c.fecha)}</span>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: c.exacto ? "#2ecc71" : "#f39c12", ...num }}>
              {fmt(c.montoTotal)}{c.exacto ? " ✓ exacto" : ""}
            </span>
            <button
              onClick={() => conciliar(m, c)}
              disabled={conciliando === m._id}
              className="no-touch-min t-02"
              style={{
                background: "#0d2a15", color: "#2ecc71", border: "1px solid #0d4020",
                borderRadius: 6, padding: "5px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer",
              }}>
              {conciliando === m._id ? "…" : "Vincular"}
            </button>
          </div>
        ))
      )}
    </div>
  );

  const badgeConc = conc => (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 20,
      color: conc ? "#2ecc71" : "#f39c12",
      background: conc ? "#0d2a15" : "#2d2000",
      border: `1px solid ${conc ? "#0d4020" : "#4a3500"}`, whiteSpace: "nowrap",
    }}>{conc ? "Conciliado" : "Pendiente"}</span>
  );

  const btnAccion = (m, conc) => conc ? (
    <button
      onClick={() => conciliar(m, null)}
      disabled={conciliando === m._id}
      className="no-touch-min t-02"
      style={{
        background: "#2a2a2a", color: "#999", border: "1px solid #3a3a3a",
        borderRadius: 8, padding: isMobile ? "8px 12px" : "4px 10px", fontSize: 11, fontWeight: 700,
        cursor: "pointer", flexShrink: 0, minHeight: isMobile ? 38 : undefined,
      }}>
      {conciliando === m._id ? "…" : "Desconciliar"}
    </button>
  ) : (
    <button
      onClick={() => abrirCandidatos(m)}
      className="no-touch-min t-02"
      style={{
        background: abierto === m._id ? "#3a1114" : "#0d2035",
        color: abierto === m._id ? "#ff8a8f" : "#3498db",
        border: `1px solid ${abierto === m._id ? "#e30613" : "#1a3a5c"}`,
        borderRadius: 8, padding: isMobile ? "8px 12px" : "4px 10px", fontSize: 11, fontWeight: 700,
        cursor: "pointer", flexShrink: 0, minHeight: isMobile ? 38 : undefined,
      }}>
      {abierto === m._id ? "Cerrar" : "Conciliar"}
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

      {/* ── Zona de subida ── */}
      <div style={S.card}>
        <div style={{ ...S.label, marginBottom: 8 }}>Subir cartola del banco (.csv o .xlsx)</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select value={bancoUp} onChange={e => setBancoUp(e.target.value)} style={{ ...S.input, flex: "1 1 140px", minWidth: 140 }}>
            <option value="santander">Santander</option>
            <option value="chile">Banco de Chile</option>
          </select>
          <label style={{
            ...S.input, flex: "2 1 200px", minWidth: 180, cursor: "pointer",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            color: archivo ? "#2ecc71" : "#8a8a8a",
          }}>
            {archivo ? archivo.name : "Elegir archivo…"}
            <input
              type="file"
              accept=".csv,.xlsx,.xls,.txt"
              style={{ display: "none" }}
              onChange={e => { setArchivo(e.target.files?.[0] || null); setResultado(null); }}
            />
          </label>
          <button className="no-touch-min t-02" onClick={subir} disabled={!archivo || subiendo}
            style={{ ...S.btnRojo(!archivo || subiendo), flex: "1 1 120px" }}>
            {subiendo ? "Subiendo…" : "⇪ Subir cartola"}
          </button>
        </div>
        {resultado && (
          <div style={{
            marginTop: 10, padding: "8px 12px", borderRadius: 8, fontSize: 12,
            background: resultado.ok ? "#0d2a15" : "#2d1515",
            border: `1px solid ${resultado.ok ? "#0d4020" : "#4a2020"}`,
            color: resultado.ok ? "#2ecc71" : "#e74c3c",
          }}>{resultado.msg}</div>
        )}
        <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 8 }}>
          Exporta la cartola desde el portal del banco tal cual (Santander: Fecha/Detalle/Cargo/Abono/Saldo ·
          Banco de Chile: cartola con cargos y abonos). Los movimientos repetidos se omiten solos.
        </div>
      </div>

      {/* ── Filtros ── */}
      <div className="chips-scroll" style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input
          type="month" value={mes}
          onChange={e => setMes(e.target.value || mesActual())}
          style={{ ...S.input, width: 150, colorScheme: "dark" }}
        />
        {[["", "Ambos bancos"], ["santander", "Santander"], ["chile", "Banco de Chile"]].map(([k, l]) => (
          <button key={k} type="button" className="chip-touch no-touch-min t-02"
            onClick={() => setFiltroBanco(k)}
            style={S.chip(filtroBanco === k)}>{l}</button>
        ))}
        <span style={{ width: 1, alignSelf: "stretch", background: "#333", flexShrink: 0 }} />
        {[["pendientes", "Pendientes"], ["conciliados", "Conciliados"], ["todos", "Todos"]].map(([k, l]) => (
          <button key={k} type="button" className="chip-touch no-touch-min t-02"
            onClick={() => setFiltroConc(k)}
            style={S.chip(filtroConc === k)}>{l}</button>
        ))}
      </div>

      {/* ── KPIs del mes ── ("Conciliado" filtra los pendientes con un toque) */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {[
          { label: "Abonos del mes", value: fmt(abonos), color: "#2ecc71" },
          { label: "Cargos del mes", value: fmt(cargos), color: "#e74c3c" },
          { label: filtroConc === "pendientes" ? "Conciliado ✕" : "Conciliado",
            value: `${pct}%`, color: pct >= 80 ? "#2ecc71" : pct >= 40 ? "#f39c12" : "#e74c3c",
            sub: filtroConc === "pendientes" ? "mostrando solo pendientes — toca para soltar" : `${conciliados} de ${movs.length} movimientos · toca para ver pendientes`,
            click: () => setFiltroConc(f => f === "pendientes" ? "todos" : "pendientes"),
            activa: filtroConc === "pendientes" },
        ].map(k => (
          <div key={k.label}
            onClick={k.click || undefined}
            className={k.click ? "t-02 tap-card" : undefined}
            style={{ ...S.card, flex: "1 1 30%", minWidth: 140,
              cursor: k.click ? "pointer" : undefined,
              border: `1px solid ${k.activa ? "#f39c12" : "#2e2e2e"}` }}>
            <div style={S.label}>{k.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: k.color, ...num }}>{k.value}</div>
            {k.sub && <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 2 }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* ── Movimientos ── */}
      {error && <div style={{ color: "#e74c3c", fontSize: 12, padding: 12 }}>Error: {error}</div>}
      {loading && <div style={{ color: "#8a8a8a", fontSize: 12, padding: 12 }}>Cargando movimientos…</div>}
      {!loading && !error && visibles.length === 0 && (
        <div style={{ ...S.card, textAlign: "center", color: "#8a8a8a", fontSize: 12 }}>
          {movs.length === 0 ? "Sin movimientos para este mes. Sube una cartola arriba." : "Nada con ese filtro ✓"}
        </div>
      )}

      {/* ── Móvil: tarjetas apiladas (como siempre) ── */}
      {isMobile && visibles.map(m => {
        const conc = !!m.conciliadoCon?.tipo;
        const det = m.conciliadoCon?.detalle;
        const esAbono = m.monto > 0;
        return (
          <div key={m._id} style={{ ...S.card, padding: "12px 14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.descripcion || "(sin detalle)"}
                </div>
                <div style={{ fontSize: 11, color: "#8a8a8a", ...num }}>
                  {fmtD(m.fecha)} · {BANCO_LABEL[m.banco] || m.banco}
                  {m.refDoc ? ` · doc ${m.refDoc}` : ""}
                  {m.saldo != null ? ` · saldo ${fmt(m.saldo)}` : ""}
                </div>
                {conc && (
                  <div style={{ fontSize: 11, color: "#2ecc71", marginTop: 3, ...num }}>
                    ↳ {m.conciliadoCon.tipo === "rendicion"
                        ? (det?.razon || "Rendición")
                        : `F°${det?.folio ?? "—"} ${det?.razon || ""}`}
                    {m.conciliadoCon.auto ? " · auto" : " · manual"}
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: esAbono ? "#2ecc71" : "#e74c3c", ...num }}>
                  {esAbono ? "+" : "−"}{fmt(m.monto)}
                </div>
                {badgeConc(conc)}
              </div>
              {btnAccion(m, conc)}
            </div>

            {/* candidatos para conciliación manual */}
            {abierto === m._id && (
              <div style={{ marginTop: 10, borderTop: "1px dashed #333", paddingTop: 10 }}>
                {renderCandidatos(m, esAbono)}
              </div>
            )}
          </div>
        );
      })}

      {/* ── Desktop: tabla densa (28-ago, patrón Pagos) con fila expandible ── */}
      {!isMobile && visibles.length > 0 && (
        <div style={{ border: "1px solid #2e2e2e", borderRadius: 8, overflowX: "auto", background: "#232323" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "80px" }} />   {/* Fecha */}
              <col />                              {/* Detalle */}
              <col style={{ width: "120px" }} />  {/* Banco */}
              <col style={{ width: "130px" }} />  {/* Monto */}
              <col style={{ width: "105px" }} />  {/* Estado */}
              <col style={{ width: "130px" }} />  {/* Acción */}
            </colgroup>
            <thead>
              <tr>
                <th style={S.th}>Fecha</th>
                <th style={S.th}>Detalle</th>
                <th style={S.th}>Banco</th>
                <th style={{ ...S.th, textAlign: "right" }}>Monto</th>
                <th style={S.th}>Estado</th>
                <th style={S.th}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((m, i) => {
                const conc = !!m.conciliadoCon?.tipo;
                const det = m.conciliadoCon?.detalle;
                const esAbono = m.monto > 0;
                const esAbierto = abierto === m._id;
                return (
                  <React.Fragment key={m._id}>
                    <tr
                      style={{ background: esAbierto ? "#2a2a2a" : i % 2 === 0 ? "#232323" : "#212121" }}
                      onMouseEnter={ev => { if (!esAbierto) Array.from(ev.currentTarget.cells).forEach(c => c.style.background = "#2a2a2a"); }}
                      onMouseLeave={ev => { if (!esAbierto) Array.from(ev.currentTarget.cells).forEach(c => c.style.background = ""); }}>
                      <td style={{ ...S.td, ...num }}>{fmtD(m.fecha)}</td>
                      <td style={{ ...S.td, color: "#fff", fontWeight: 700 }} title={m.descripcion || ""}>
                        {m.descripcion || "(sin detalle)"}
                        {conc && (
                          <span style={{ color: "#2ecc71", fontWeight: 400, fontSize: 11, ...num }}>
                            {" "}↳ {m.conciliadoCon.tipo === "rendicion"
                              ? (det?.razon || "Rendición")
                              : `F°${det?.folio ?? "—"} ${det?.razon || ""}`}
                            {m.conciliadoCon.auto ? " · auto" : " · manual"}
                          </span>
                        )}
                      </td>
                      <td style={S.td}>{BANCO_LABEL[m.banco] || m.banco}</td>
                      <td style={{ ...S.td, textAlign: "right", fontWeight: 800, color: esAbono ? "#2ecc71" : "#e74c3c", ...num }}>
                        {esAbono ? "+" : "−"}{fmt(m.monto)}
                      </td>
                      <td style={{ ...S.td, overflow: "visible" }}>{badgeConc(conc)}</td>
                      <td style={{ ...S.td, overflow: "visible" }}>{btnAccion(m, conc)}</td>
                    </tr>
                    {esAbierto && (
                      <tr>
                        <td colSpan={6} style={{ padding: "10px 14px", background: "#1f1f1f", borderBottom: "1px solid #2e2e2e" }}>
                          {renderCandidatos(m, esAbono)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Cartolas;
